import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';

import { Box } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip from '@mui/material/Tooltip';
import type { CSSProperties } from '@mui/styles';

export const OverflowTooltip: FC<TooltipProps> = ({ title, children, sx, ...other }) => {
  const [isOpen, setIsOpen] = useState(false);

  const textElementRef = useRef<HTMLDivElement>(null);

  // Measured at the moment MUI wants to open, rather than on mount or on hover.
  //
  // Mount-only measurement goes stale as soon as the container is resized (dragging a resizable grid
  // column is the common case), leaving truncated text with no tooltip. Re-measuring in `onMouseEnter`
  // doesn't fix it either: MUI has already decided whether to open using the previous render's
  // `disableHoverListener`, so a re-measure there only takes effect on the *next* hover. Deciding
  // inside `onOpen` is always current, and costs nothing per rendered cell the way a ResizeObserver
  // would.
  const handleOpen = useCallback(() => {
    const element = textElementRef.current;
    if (element && element.scrollWidth > element.clientWidth) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <Tooltip
      title={<div style={{ whiteSpace: 'pre-line' }}>{title}</div>}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      {...other}
    >
      <Box
        sx={(theme) =>
          ({
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(typeof sx === 'function' ? sx(theme) : sx)
          }) as CSSProperties
        }
        ref={textElementRef}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
